import { useQuery } from "@apollo/client"
import { debounce } from "lodash-es"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import Checkbox from "./checkbox"
import { graphql } from "@/api"
import { type PriceQuery, type PriceQueryVariables } from "@/api/graphql"
import ClientData, { type EditingState } from "@/common/client_data"
import env from "@/common/env"
import { isTablet } from "@/common/util"
import Sign from "@/signomatic/app/sign"
import { type State } from "@/signomatic/store/reducers"

const priceDoc = graphql(`
  query Price($signcode: String!, $numberOfSigns: String, $currentCount: Int, $editingId: Int) {
    price(signcode: $signcode, numberOfSigns: $numberOfSigns, currentCount: $currentCount, editingId: $editingId) {
      regularPrice
      discountedPrice
      discountPercent
    }
  }
`)

interface PriceOwnProps {
  sign: Sign
  numberOfSigns?: number
  currentCount?: number
  editingId?: number
}

interface PriceStateProps {
  editing: EditingState | null
  signState: SignState | null
}

type PriceProps = PriceStateProps & PriceOwnProps

const Price: React.FC<PriceProps> = ({ sign, editing, numberOfSigns, currentCount, signState: propSignState }) => {
  const [signState, setSignState] = useState<SignState | null>(null)
  const [signcode, setSigncode] = useState<string | null>(null)
  const [signType, setSignType] = useState<string | null>(null)
  const { t } = useTranslation()

  const { data, loading, error, previousData } = useQuery<PriceQuery, PriceQueryVariables>(priceDoc, {
    variables: {
      signcode: signcode || "",
      numberOfSigns: numberOfSigns?.toString(),
      currentCount,
      editingId: editing?.id,
    },
    skip: !signcode,
  })

  const statePricedChanged = useCallback(
    (newState: SignState) => {
      const currentSignType = sign.signType

      if (!signState || !currentSignType) {
        return true
      }

      if (currentSignType !== signType) {
        return true
      }

      // size
      if (!["color_badge", "brass_sign"].includes(currentSignType)) {
        if (newState.width !== signState.width || newState.height !== signState.height) {
          return true
        }
      }

      // images
      if (!["color_badge", "brass_sign", "rollup"].includes(currentSignType)) {
        const oldCount = signState.objects.filter(obj => Sign.isImage(obj)).length
        const newCount = newState.objects.filter(obj => Sign.isImage(obj)).length

        if (oldCount !== newCount) {
          return true
        }
      }

      // fixmethod
      if (!["banner", "vinyl_text", "magnetic_sign"].includes(currentSignType)) {
        if (signState.fixmethod !== newState.fixmethod) {
          return true
        }
      }

      // border
      if (!["color_badge", "brass_sign", "rollup"].includes(currentSignType)) {
        if (signState.border !== newState.border) {
          return true
        }
      }

      // background
      if (["plastic_sign", "name_sign", "aluminium_sign", "double_sided_sign", "wood_sign"].includes(currentSignType)) {
        if (signState.color !== newState.color) {
          return true
        }
      }

      // material
      if (
        ["rollup", "color_badge", "brass_sign", "aluminium_sign", "double_sided_sign", "label_plates"].includes(
          currentSignType
        )
      ) {
        if (signState.material !== newState.material) {
          return true
        }
      }

      // thickness
      if (["plastic_sign"].includes(currentSignType)) {
        if (signState.thickness !== newState.thickness) {
          return true
        }
      }

      if (signState.badgeName !== newState.badgeName) {
        return true
      }

      if (signState.doubleSided !== newState.doubleSided) {
        return true
      }

      return
    },
    [sign.signType, signState, signType]
  )

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        async (state: SignState) => {
          let newSigncode = signcode

          state.width = state.forcedWidth || state.width
          state.height = state.forcedHeight || state.height

          if (statePricedChanged(state)) {
            const code = await sign.signcode(true)
            if (code) newSigncode = JSON.stringify(code)
          }

          setSignState(state)
          setSignType(sign.signType)
          setSigncode(newSigncode)
        },
        300,
        { leading: false }
      ),
    [sign, signcode, statePricedChanged]
  )

  useEffect(() => {
    const fetchSignCode = async () => {
      if (!sign.state) return

      const code = await sign.signcode(true)
      const signcode = code ? JSON.stringify(code) : null
      setSigncode(signcode)
    }

    fetchSignCode()
    sign.onSignChanged.subscribe(debouncedUpdate)

    return () => {
      sign.onSignChanged.unsubscribe(debouncedUpdate)
    }
  }, [debouncedUpdate, sign])

  const PriceCheckbox = () => {
    const disabled = (editing && editing.fixedPrice) || false

    if (!editing || !editing.admin || !propSignState) {
      return null
    }

    return (
      <div className="option">
        <Checkbox
          id="sign_price_fixed"
          checked={propSignState.fixedPrice}
          onChange={() => sign.updateState({ fixedPrice: !propSignState.fixedPrice })}
          disabled={disabled}
        />
        <label htmlFor="sign_price_fixed">Pris låst</label>
      </div>
    )
  }

  const vatText = ClientData.businessRole ? t("order.exc_vat") : t("order.inc_vat")

  if (editing && (editing.fixedPrice || (propSignState && propSignState.fixedPrice))) {
    return <PriceCheckbox />
  }

  if (!signcode) {
    return null
  }

  const currentData = data || previousData
  if (error || !currentData || !currentData.price) {
    return null
  }

  const regularPrice = env.TEST && loading ? "--" : currentData.price.regularPrice

  const showDiscount = currentData.price.discountedPrice && !editing

  let mainPrice = regularPrice
  if (currentData.price.discountedPrice && !editing) {
    mainPrice = currentData.price.discountedPrice
  }

  return (
    <>
      {isTablet() ? (
        <div id="sign_price_field" className="flex flex-col">
          {sign.signType !== "" && (
            <h1 className="sign-type paragraph-2xs bold right mb-0 self-end">
              {t(`sign_type.${propSignState?.metaProduct ? propSignState.metaProduct : sign.signType}`)}
            </h1>
          )}
          <div className="flex items-baseline self-end">
            <span className="paragraph-sm bold mb-0 mr-1 whitespace-nowrap">{mainPrice}</span>
            <span className="paragraph-2xs mb-0 whitespace-nowrap">{vatText}</span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex flex-row items-center">
            <div id="sign_price_field" className="mb-0 mr-2 text-base font-semibold xl:text-xl">
              {mainPrice}
            </div>

            {showDiscount && (
              <>
                <div
                  id="regular_price_field"
                  className="mb-0 mr-2 whitespace-nowrap text-base leading-6 text-ink-50 line-through xl:text-xl"
                >
                  {regularPrice}
                </div>

                <div
                  id="discount_percent_field"
                  className="blue-badge group flex h-6 min-w-fit items-center whitespace-nowrap rounded bg-blue-200"
                >
                  <div className="px-2">
                    <div className="text-xs font-semibold text-blue-600">
                      {t("order.discount_badge", { discount: currentData.price.discountPercent })}%
                    </div>
                  </div>
                  <div className="discount-tooltip height-[55px] absolute bottom-[66px] hidden w-[310px] rounded-lg bg-ink-85 px-3 py-2 group-hover:block">
                    <div className="absolute -bottom-[14%] left-[17px] origin-center rotate-45 border-x-[8px] border-t-[16px] border-solid border-transparent bg-ink-85"></div>
                    <span className="tooltip-text paragraph-2xs light mb-0 block whitespace-normal decoration-white">
                      {t("order.discount_tooltip")}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="my-[1px] flex items-center">
            <span className="whitespace-nowrap text-xs">{t("order.price_per")}</span>
            <span className="mx-1 font-['Lato'] text-xs">&#183;</span>
            <span className="whitespace-nowrap text-xs">{vatText}</span>
          </div>
        </div>
      )}
    </>
  )
}

export default connect(
  ({ editing, signState }: State): PriceStateProps => ({
    editing,
    signState,
  }),
  {}
)(Price)
